import React, { useEffect, useState, useCallback } from 'react';
import { Dialog, DialogTitle, DialogContent, IconButton, Tooltip, Tabs, Tab, Box, Button, Chip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import { getErrorLogs, clearErrorLogs } from '../../utils/errorLogger.js';

export default function ErrorLogViewer() {
  const [open, setOpen] = useState(false);
  const [logs, setLogs] = useState([]);
  const [levelFilter, setLevelFilter] = useState('all');
  const [activeTab, setActiveTab] = useState(0);

  const refresh = useCallback(() => {
    setLogs(getErrorLogs());
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (e.ctrlKey && e.altKey && e.key.toLowerCase() === 'l') {
        refresh();
        setOpen(o => !o);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [refresh]);

  const filtered = logs.filter(l => levelFilter === 'all' || l.level === levelFilter);

  const copyAll = () => {
    navigator.clipboard.writeText(JSON.stringify(filtered, null, 2)).catch(()=>{});
  };

  const clear = () => {
    clearErrorLogs();
    refresh();
  };

  return (
    <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="lg">
      <DialogTitle
        sx={{
          pr: { xs: 7, sm: 6 },
          overflowWrap: 'anywhere',
          wordBreak: 'break-word',
        }}
      >
        Client Error Logs ({filtered.length})
        <IconButton
          aria-label="Закрыть"
          onClick={() => setOpen(false)}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            minWidth: 44,
            minHeight: 44,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            px: 2,
            py: 1,
            gap: 1,
          }}
        >
          <Tabs value={activeTab} onChange={(_,v)=>setActiveTab(v)} sx={{ minHeight: 44 }}>
            <Tab label="Logs" />
            <Tab label="Meta" />
          </Tabs>
          <Box sx={{ flexGrow: 1 }} />
          <Tooltip title="Copy JSON">
            <IconButton
              onClick={copyAll}
              aria-label="Скопировать JSON"
              sx={{ minWidth: 44, minHeight: 44 }}
            >
              <ContentCopyIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Clear logs">
            <IconButton
              onClick={clear}
              aria-label="Очистить логи"
              sx={{ minWidth: 44, minHeight: 44 }}
            >
              <DeleteSweepIcon />
            </IconButton>
          </Tooltip>
          <Button sx={{ minHeight: 44 }} onClick={refresh}>
            Refresh
          </Button>
        </Box>
        {activeTab === 0 && (
          <Box sx={{ p: 2, maxHeight: '60vh', overflow: 'auto', fontFamily: 'monospace', fontSize: 12 }}>
            <Box sx={{ mb: 1, display: 'flex', gap: 1 }}>
              {['all','error','warn','info'].map(l => (
                <Chip
                  key={l}
                  label={l}
                  color={levelFilter===l? 'primary':'default'}
                  onClick={()=>setLevelFilter(l)}
                  sx={{ minHeight: 36 }}
                />
              ))}
            </Box>
            {filtered.length === 0 && <div style={{ opacity: 0.6 }}>No logs</div>}
            {filtered.map((e,i) => (
              <Box key={i} sx={{ mb: 2, borderBottom: '1px solid #eee', pb: 1 }}>
                <div><strong>{e.time}</strong> [{e.level||'error'}] {e.name}: {e.message}</div>
                {e.stack && <pre style={{ whiteSpace: 'pre-wrap', margin: '4px 0' }}>{e.stack}</pre>}
                {e.extra && <pre style={{ background:'#fafafa', padding:4 }}>{JSON.stringify(e.extra,null,2)}</pre>}
              </Box>
            ))}
          </Box>
        )}
        {activeTab === 1 && (
          <Box sx={{ p:2 }}>
            <pre style={{ fontSize:12 }}>{JSON.stringify({ userAgent: navigator.userAgent }, null, 2)}</pre>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
